import { useEffect, useState } from 'react';
import AppHeader from '../../components/AppHeader';
import { useAuth } from '../../contexts/MemberAuthContext';
import { vehiclesApi, chaptersApi } from '../../api';

export const VEHICLE_TYPES = {
  car: 'Osobní', van: 'Dodávka', truck: 'Nákladní', bus: 'Autobus',
  ambulance: 'Sanitka', trailer: 'Přívěs', other: 'Jiné',
};

const emptyForm = { name: '', plate: '', type: 'van', fuel_type: '', capacity_note: '', active: true };

export default function Vehicles() {
  const { member } = useAuth();
  const canManage = ['superadmin', 'chapter_rep'].includes(member.role);
  const isOps = ['superadmin', 'ops'].includes(member.role);

  const [chapters, setChapters] = useState([]);
  const [chapterId, setChapterId] = useState(member.chapter_id || '');
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOps) chaptersApi.list().then((r) => {
      setChapters(r.data);
      if (!chapterId && r.data.length) setChapterId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = () => {
    // OpS/superadmin bez vybraného spolku → vše; jinak konkrétní spolek
    const scope = isOps ? chapterId : (member.chapter_id || '');
    vehiclesApi.list(scope || undefined).then((r) => setVehicles(r.data)).catch(() => setVehicles([]));
  };
  useEffect(() => { load(); }, [chapterId]);

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const targetChapter = member.role === 'chapter_rep' ? member.chapter_id : chapterId;
    if (!targetChapter) { setError('Vyberte oblastní spolek.'); return; }
    try {
      if (editId) await vehiclesApi.update(editId, form);
      else await vehiclesApi.create({ ...form, chapter_id: targetChapter });
      resetForm();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Uložení selhalo.');
    }
  };

  const edit = (v) => {
    setEditId(v.id); setShowForm(true); setError('');
    setForm({
      name: v.name, plate: v.plate || '', type: v.type || 'other',
      fuel_type: v.fuel_type || '', capacity_note: v.capacity_note || '', active: !!v.active,
    });
  };

  const remove = async (id) => {
    if (!confirm('Opravdu smazat vozidlo? Smažou se i jeho cesty.')) return;
    await vehiclesApi.delete(id);
    load();
  };

  return (
    <div>
      <AppHeader subtitle="Vozidla" />
      <div className="container-wide" style={{ paddingTop: 24 }}>

        {isOps && (
          <div className="form-group" style={{ maxWidth: 360 }}>
            <label>Oblastní spolek</label>
            <select className="form-input" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
              <option value="">— všechny spolky —</option>
              {chapters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {!isOps && !member.chapter_id && <div className="alert alert-info">Nemáte přiřazený žádný spolek.</div>}

        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2>Vozidla ({vehicles.length})</h2>
          {canManage && !showForm && (member.role === 'chapter_rep' ? member.chapter_id : true) && (
            <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); }}>+ Přidat vozidlo</button>
          )}
        </div>

        {showForm && canManage && (
          <div className="card mb-16">
            <h3 className="mb-16">{editId ? 'Upravit vozidlo' : 'Nové vozidlo'}</h3>
            {isOps && !editId && (
              <div className="form-group">
                <label>Spolek vozidla</label>
                <select className="form-input" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
                  <option value="">— vyberte spolek —</option>
                  {chapters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <form onSubmit={submit}>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Název / označení</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-group flex-1">
                  <label>SPZ</label>
                  <input className="form-input" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Typ</label>
                  <select className="form-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    {Object.entries(VEHICLE_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Palivo</label>
                  <input className="form-input" placeholder="nafta / benzín / elektro" value={form.fuel_type} onChange={(e) => setForm({ ...form, fuel_type: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Kapacita / poznámka</label>
                <input className="form-input" value={form.capacity_note} onChange={(e) => setForm({ ...form, capacity_note: e.target.value })} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Vozidlo je v provozu</span>
              </label>
              {error && <div className="alert alert-warning mb-16">{error}</div>}
              <div className="flex gap-8">
                <button type="submit" className="btn btn-primary flex-1">{editId ? 'Uložit' : 'Přidat'}</button>
                <button type="button" className="btn btn-outline" onClick={resetForm}>Zrušit</button>
              </div>
            </form>
          </div>
        )}

        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Vozidlo</th><th>SPZ</th><th>Typ</th><th>Palivo</th>{isOps && <th>Spolek</th>}<th>Otevřené cesty</th>{canManage && <th></th>}</tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id}>
                  <td>{v.name}{!v.active && <span className="badge badge-none" style={{ marginLeft: 6 }}>mimo provoz</span>}</td>
                  <td>{v.plate || ''}</td>
                  <td>{VEHICLE_TYPES[v.type] || v.type}</td>
                  <td>{v.fuel_type || ''}</td>
                  {isOps && <td>{v.chapter_name || ''}</td>}
                  <td>{v.open_trips || 0}</td>
                  {canManage && (
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" onClick={() => edit(v)}>Upravit</button>{' '}
                      <button className="btn btn-danger btn-sm" onClick={() => remove(v.id)}>Smazat</button>
                    </td>
                  )}
                </tr>
              ))}
              {vehicles.length === 0 && <tr><td colSpan={(isOps ? 6 : 5) + (canManage ? 1 : 0)} className="text-muted text-center">Žádná vozidla.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
